// Imports
import Chart from 'chart.js/auto';
import * as ss from 'simple-statistics';
import 'chartjs-adapter-moment';
import {trans} from "../i18n/i18n";
import {histogram} from "./histogram";
import {minPriceVsOdoChartsResults} from "../configs/VD-config";
import colors from "../../sass/base/variables/colors.module.scss";

const { chartTrendline, chartAboveMarket, chartBelowMarket, chartMarketAverage } = colors;

// Constants
const CURRENCY = "€";
const LENGTH_UNIT = "km";

// Functions
/**
 * Determine if a point is above or below the trend line
 */
function getPointColorTrendLine(odometerArray, priceArray, trendLine) {
    return odometerArray.map((odoValue, index) => {
        const priceValue = priceArray  [index];
        const trendY = getTrendLineY(odoValue, trendLine);
        return priceValue > trendY ? chartAboveMarket : chartBelowMarket;
    });
}

function setAvgVehiclePriceDescription({ below, above, avg, make, model, year, avgOdo, records }) {
    const el = document.getElementById("js-avgVehiclePrice-description");
    if (!el) return;

    const minStr = `${Math.round(below).toLocaleString()} ${CURRENCY}`;
    const maxStr = `${Math.round(above).toLocaleString()} ${CURRENCY}`;
    const avgStr = `${Math.round(avg).toLocaleString()} ${CURRENCY}`;
    const recordsStr = Math.round(records).toLocaleString();

    el.textContent = trans(
        "chart.Given an average odometer reading of :odo, the market price for a :make :model, :year is typically between :min (Below market) and :max (Above market), with the average price being :avg, based on data from :records records across Europe.",
        {
            odo: avgOdo,
            make,
            model,
            year,
            min: minStr,
            max: maxStr,
            avg: avgStr,
            records: recordsStr
        }
    );

    el.classList.remove("d-none");
}


/**
 * Generate an array of numbers within a range
 * @param {number} start - The starting value of the range
 * @param {number} end - The ending value of the range
 * @param {number} step - The step between values in the range (default: 1)
 * @returns {Array} - An array of numbers within the specified range
 */
function range(start, end, step = 1) {
    // Calculate the number of elements needed in the array
    const len = Math.floor((end - start) / step) + 1;

    // Fill an array of that length with values based on the start, end, and step parameters
    return Array(len).fill().map((_, idx) => start + (idx * step));
}

/**
 * Calculate the percentile value of a given list of numbers
 * @param {number} percentile - The percentile to calculate (e.g. 0.5 for 50th percentile)
 * @param {Array} values - The list of numbers to calculate the percentile from
 * @returns {number} - The percentile value
 */
function calculatePercentile(percentile, values) {
    // Step 1: Sort the array in ascending order
    values.sort(function (a, b) {
        return a - b;
    });

    // Step 2: Determine the index of the desired percentile value
    var index = Math.ceil(percentile * values.length) - 1;

    // Return the value at that index in the sorted array
    return values[index];
}

/**
 * Calculates the y value for a given x value based on a specified linear trendline
 * @param x
 * @param trendline
 * @returns {*}
 */
function getTrendLineY(x, trendline) {
    return trendline.m * x + trendline.b;
}

/**
 * Get color of Points based on year
 * @param yearsArray
 * @param year
 * @param defaultColor
 * @param highlightedColor
 * @returns {*}
 */
function getPointBackgroundColorByYear(yearsArray, year, defaultColor, highlightedColor) {
    return yearsArray.map(y => y == year ? highlightedColor : defaultColor);
}

/**
 * Get radius of Points based on year
 * @param yearsArray
 * @param year
 * @param defaultRadius
 * @param highlightedRadius
 * @returns {*}
 */
function getPointRadiusByYear(yearsArray, year, defaultRadius, highlightedRadius) {
    return yearsArray.map(y => y == year ? highlightedRadius : defaultRadius);
}

/**
 * Returns the index of the given year in the yearsArray.
 * @param {Array} yearsArray - Array containing years.
 * @param {number} year - The target year to find.
 * @returns {number} - Index of the target year in yearsArray, -1 if not found.
 */
function getHighlightedIndex(yearsArray, year) {
    return yearsArray.findIndex(y => y == year);
}

/**
 * * Sets a persistent tooltip highlight on the chart for specific data points corresponding to a given year.
 * @param chartInstance
 * @param yearsArray
 * @param year
 */
function setHighlightTooltip(chartInstance, yearsArray, year) {
    if (!year || !Array.isArray(yearsArray) || !chartInstance) return;

    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    if (width < 768) return;

    const idx = getHighlightedIndex(yearsArray, year);
    if (idx < 0) return;

    const metas = chartInstance.getDatasetMeta(0);
    const metas2 = chartInstance.getDatasetMeta(1);
    if (!metas?.data?.[idx] || !metas2?.data?.[idx]) return;

    chartInstance.setActiveElements(
        [
            { datasetIndex: 0, index: idx },
            { datasetIndex: 1, index: idx }
        ]
    );
    chartInstance.update();
}

/**
 * Render a responsive half-doughnut gauge for "Average Vehicle Price".
 */
export function renderAverageVehiclePriceGauge({ data, make, model, year }) {
    // Compute inputs from data
    const rows = Array.isArray(data?.priceAndOdo?.details) ? data.priceAndOdo.details : [];
    const prices = rows.map(r => Number(r.price_original)).filter(v => Number.isFinite(v) && v > 0);
    const odos   = rows.map(r => Number(r.odometer_km)).filter(v => Number.isFinite(v) && v > 0);

    if (!prices.length) {
        const $canvas = document.getElementById("js-avgVehiclePrice");
        const $section = document.getElementById("js-avgVehiclePrice-section");
        if ($canvas) {
            try { $canvas.chartInstance?.destroy(); } catch {}
            $canvas.classList.add("d-none");
            $canvas.parentElement?.classList.add("d-none");
        }
        $section?.classList.add("d-none");
        document.getElementById("js-avgVehiclePrice-description")?.classList.add("d-none");
        return;
    }

    const avg = Math.round(prices.reduce((a, b) => a + b, 0) / prices.length);
    const below = Math.round(calculatePercentile(0.15, [...prices]) / 100) * 100;
    const above = Math.round(calculatePercentile(0.75, [...prices]) / 100) * 100;

    const avgOdo = odos.length
        ? `${Math.round(odos.reduce((a, b) => a + b, 0) / odos.length).toLocaleString()} km`
        : null;
    
    const count = prices.length;

    const CANVAS_ID = "js-avgVehiclePrice";
    const SECTION_ID = "js-avgVehiclePrice-section";
    const MEDIA_QUERY = "(max-width: 576px)";

    const $canvas = document.getElementById(CANVAS_ID);
    const $section = document.getElementById(SECTION_ID);
    if (!$canvas) { console.error(`Canvas #${CANVAS_ID} not found`); return; }

    const valid =
        Number.isFinite(below) &&
        Number.isFinite(above) &&
        Number.isFinite(avg) &&
        above > below;

    if (!valid) {
        try { $canvas.chartInstance?.destroy(); } catch {}
        $canvas.classList.add("d-none");
        $canvas.parentElement?.classList.add("d-none");
        $section?.classList.add("d-none");
        document.getElementById("js-avgVehiclePrice-description")?.classList.add("d-none");
        console.warn("AverageVehiclePriceGauge: invalid inputs; gauge not rendered.");
        return;
    }

    // show section now that inputs are valid
    $canvas.classList.remove("d-none");
    $canvas.parentElement?.classList.remove("d-none");
    $section?.classList.remove("d-none");

    // canvas sizing
    Object.assign($canvas.style, {
        display: "block",
        width: "100%",
        maxWidth: "720px",
        minWidth: "360px",
        margin: "0 auto",
        height: ""
    });

    const fraction = Math.max(0, Math.min(1, (avg - below) / (above - below)));

    const SEGMENTS = [18, 20, 22, 22, 20, 18];
    const COLORS   = ["#12B76A","#6CE09A","#F9D24A","#F4B740","#F0856C","#EF4444"];

    const currency = (typeof CURRENCY !== "undefined" ? CURRENCY : "€");
    const formatMoney = (n) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n) + " " + currency;

    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const toXY = (cx, cy, r, ang) => ({ x: cx + r * Math.cos(ang), y: cy - r * Math.sin(ang) });

    const geom = (chart) => {
        const arc0 = chart.getDatasetMeta(0).data[0];
        return arc0 ? { cx: arc0.x, cy: arc0.y, inner: arc0.innerRadius, outer: arc0.outerRadius } : null;
    };

    function drawWrappedCenteredText(ctx, text, cx, y, maxWidth, font, lineHeight, maxLines = 2) {
        ctx.font = font;
        const words = text.split(/\s+/);
        const lines = [];
        let current = "";

        for (let i = 0; i < words.length; i++) {
            const test = current ? current + " " + words[i] : words[i];
            if (ctx.measureText(test).width <= maxWidth) {
                current = test;
            } else {
                if (current) lines.push(current);
                current = words[i];
                if (lines.length === maxLines - 1) break;
            }
        }
        if (current && lines.length < maxLines) lines.push(current);

        lines.forEach((ln, idx) => ctx.fillText(ln, cx, y + idx * lineHeight));
        return y + (lines.length - 1) * lineHeight;
    }

    function buildChart() {
        try { $canvas.chartInstance?.destroy(); } catch {}

        const data = {
            datasets: [{
                data: SEGMENTS,
                backgroundColor: COLORS,
                borderWidth: 0,
                cutout: "60%",
                radius: "100%",
                hoverBackgroundColor: COLORS,
                hoverBorderColor: "transparent",
                hoverBorderWidth: 0,
                hoverOffset: 0
            }]
        };

        const annotations = {
            needle: {
                type: 'line',
                borderColor: '#111827',
                borderWidth: 2,
                display: true,

                xMin: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 0;
                    }
                    return g.cx;
                },

                yMin: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 0;
                    }
                    return g.cy;
                },

                xMax: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 0;
                    }
                    const angle = Math.PI - Math.PI * fraction;
                    const r = g.outer * 0.95;
                    return g.cx + r * Math.cos(angle);
                },

                yMax: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 0;
                    }
                    const angle = Math.PI - Math.PI * fraction;
                    const r = g.outer * 0.95;
                    return g.cy - r * Math.sin(angle);
                },
            },

            cap: {
                type: 'point',
                backgroundColor: '#111827',

                radius: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 6;
                    }
                    return clamp(g.outer / 30, 5, 8);
                },

                xValue: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 0;
                    }
                    return g.cx;
                },

                yValue: (ctx) => {
                    const g = geom(ctx.chart);
                    if (!g) {
                        return 0;
                    }
                    return g.cy;
                },
            },
        };

        const CenterPlugin = {
            id: 'avgGaugeCenter',
            afterDatasetsDraw(chart) {
                const g = geom(chart); if (!g) return;
                const { cx, cy, inner, outer } = g;
                const c = chart.ctx;

                // inner base (no shadows)
                const baseR = inner + (outer - inner) * 0.06;
                c.save();
                c.beginPath();
                c.moveTo(cx, cy);
                c.arc(cx, cy, baseR, Math.PI, 0, false);
                c.closePath();
                c.fillStyle = "#FFFFFF";
                c.shadowColor = "transparent";
                c.shadowBlur = 0;
                c.strokeStyle = "transparent";
                c.fill();
                c.restore();

                // bubbles
                const rMid = (inner + outer) / 2;
                const pTop = toXY(cx, cy, inner, Math.PI / 2);
                const rTop  = clamp(outer/22, 6, 10);
                const rSide = clamp(outer/28, 5, 8);
                c.save();
                c.beginPath(); c.arc(pTop.x,   pTop.y, rTop,  0, Math.PI*2); c.fillStyle="#FFFFFF"; c.fill();
                c.beginPath(); c.arc(cx - rMid, cy, rSide, 0, Math.PI*2); c.fill();
                c.beginPath(); c.arc(cx + rMid, cy, rSide, 0, Math.PI*2); c.fill();
                c.restore();

                // center text block
                const fsPrice = clamp(outer * 0.15, 16, 32);
                const fsTitle = clamp(outer * 0.075,10, 16);
                const fsSmall = clamp(outer * 0.062, 9, 14);
                const gapSmall = clamp(outer * 0.06,  8, 16);
                const gapBig   = clamp(outer * 0.085,12, 22);

                let y = cy - inner * 0.56;
                c.save();
                c.textAlign = "center";

                c.fillStyle = "#F59E0B";
                c.font = `700 ${fsPrice}px ui-sans-serif, system-ui`;
                c.fillText(formatMoney(avg), cx, y);
                y += gapBig;

                const title = trans("chart.Average Vehicle Price").toUpperCase();
                c.fillStyle = "#111827";
                c.font = `800 ${fsTitle}px ui-sans-serif, system-ui`;
                const maxTxtW = Math.max(120, inner * 1.8);
                const lastY = drawWrappedCenteredText(c, title, cx, y, maxTxtW, c.font, Math.max(14, fsTitle + 2), 2);
                y = lastY + gapBig;

                const carText = [make, model, year].filter(Boolean).join(" ");
                if (carText) {
                    c.fillStyle = "#6B7280";
                    c.font = `${fsSmall}px ui-sans-serif, system-ui`;
                    c.fillText(carText, cx, y);
                    y += gapSmall;
                }
                if (avgOdo) {
                    const odoPrefix = trans("chart.Average odometer (:unit)", { "unit": LENGTH_UNIT });
                    c.fillStyle = "#6B7280";
                    c.font = `${fsSmall}px ui-sans-serif, system-ui`;
                    c.fillText(`${odoPrefix}: ${avgOdo}`, cx, y);
                }
                c.restore();

                // bottom labels
                const priceOffset = clamp(outer * 0.12, 14, 26);
                const labelGapB   = clamp(outer * 0.10, 12, 22);
                const fsPriceLR   = clamp(outer * 0.075, 12, 16);
                const fsLabelLR   = clamp(outer * 0.065, 11, 14);

                const belowTxt = trans("chart.Below market");
                const aboveTxt = trans("chart.Above market");

                c.save();
                c.textAlign = "center";
                c.font = `700 ${fsPriceLR}px ui-sans-serif, system-ui`; c.fillStyle = "#12B76A";
                c.fillText(formatMoney(below), cx - rMid, cy + priceOffset);
                c.font = `800 ${fsLabelLR}px ui-sans-serif, system-ui`; c.fillStyle = "#111827";
                c.fillText(belowTxt.toUpperCase(), cx - rMid, cy + priceOffset + labelGapB);

                c.font = `700 ${fsPriceLR}px ui-sans-serif, system-ui`; c.fillStyle = "#EF4444";
                c.fillText(formatMoney(above), cx + rMid, cy + priceOffset);
                c.font = `800 ${fsLabelLR}px ui-sans-serif, system-ui`; c.fillStyle = "#111827";
                c.fillText(aboveTxt.toUpperCase(), cx + rMid, cy + priceOffset + labelGapB);
                c.restore();
            }
        };

        const mq = window.matchMedia(MEDIA_QUERY);
        const getAspect = () => (mq.matches ? 2.0 : 2.35);

        const chart = new Chart($canvas.getContext("2d"), {
            type: 'doughnut',
            data,
            options: {
                responsive: true,
                maintainAspectRatio: true,
                aspectRatio: getAspect(),
                rotation: -90,
                circumference: 180,
                animation: { duration: 0 },
                layout: { padding: { top: 8, bottom: 72, left: 16, right: 16 } },
                events: [],
                plugins: { legend: { display: false }, tooltip: { enabled: false }, annotation: { annotations } }
            },
            plugins: [CenterPlugin]
        });

        $canvas.chartInstance = chart;

        const onMQ = () => { chart.options.aspectRatio = getAspect(); chart.resize(); chart.update('none'); };
        mq.addEventListener ? mq.addEventListener("change", onMQ) : mq.addListener(onMQ);

        return chart;
    }

    // initial render
    let chart = buildChart();
    setAvgVehiclePriceDescription({ below, above, avg, make, model, year, avgOdo, records:count });

    const forceRebuild = () => {
        $canvas.width = 0; $canvas.height = 0;
        try { $canvas.chartInstance?.destroy(); } catch {}
        chart = buildChart();
    };

    const host = $section || $canvas.parentElement || $canvas;
    new ResizeObserver(() => forceRebuild()).observe(host);
    window.addEventListener("resize", forceRebuild, { passive: true });
    new MutationObserver(() => forceRebuild()).observe(host, { attributes: true, attributeFilter: ["class", "style"] });
}

/**
 * Render graph to show average odometer and price by year of construction
 * @param data
 * @param make
 * @param model
 * @param year
 */
export function renderAvgPriceOdoChart({data, make, model, year}) {
    if (!data) {
        console.error("Invalid or missing data for chart rendering.");
        return;
    }

    const avgPriceOdoChartElm = document.getElementById("js-avgPriceOdoChart");
    if (avgPriceOdoChartElm === null) {
        console.error("The element with ID 'js-avgPriceOdoChart' was not found in the document!");
        return;
    }

    const ctx = avgPriceOdoChartElm.getContext("2d");

    const yearsArray = [];
    const avgOdoArray = [];
    const avgPriceArray = [];

    data.map((graphEntry) => {
        yearsArray.push(graphEntry.date_manufactured);
        avgOdoArray.push(Math.floor(graphEntry["avg_odometer"]));
        avgPriceArray.push(Math.floor(graphEntry["avg_euro_price"]));
    });

    const avgPricePointColors = getPointBackgroundColorByYear(yearsArray, year, "rgb(75, 192, 192)", "rgb(0, 0, 0)");
    const avgOdoPointColors = getPointBackgroundColorByYear(yearsArray, year, "#3a73ff", "rgb(0, 0, 0)");
    const avgPricePointRadii = getPointRadiusByYear(yearsArray, year, 3, 6);
    const avgOdoPointRadii = getPointRadiusByYear(yearsArray, year, 3, 6);

    const chartData = {
        labels: yearsArray,
        datasets: [{
            label: trans("chart.Average price (:currency)", {"currency": CURRENCY}),
            yAxisID: "left-y-axis",
            data: avgPriceArray,
            borderColor: "rgb(75, 192, 192)",
            backgroundColor: "rgb(75, 192, 192, 0.1)",
            tension: 0.1,
            pointBackgroundColor: avgPricePointColors,
            pointRadius: avgPricePointRadii,
        }, {
            label: trans("chart.Average odometer (:unit)", {"unit": LENGTH_UNIT}),
            yAxisID: "right-y-axis",
            data: avgOdoArray,
            borderColor: "#3a73ff",
            backgroundColor: "rgb(58, 115, 255, 0.1)",
            tension: 0.1,
            pointBackgroundColor: avgOdoPointColors,
            pointRadius: avgOdoPointRadii,
        }],
    };
    const chartOptions = {
        interaction: {
            intersect: false,
            mode: 'index'
        },
        responsive: true,
        hoverMode: 'index',
        stacked: false,
        title: {
            display: false,
        },
        scales: {
            'left-y-axis': {
                type: 'linear',
                stacked: true,
                position: 'left',
                title: {
                    display: true,
                    text: '€',
                    padding: {top: 0, left: 0, right: 0, bottom: 10}
                }
            },
            'right-y-axis': {
                grid: {
                    display: false
                },
                type: 'linear',
                position: 'right',
                title: {
                    display: true,
                    text: 'Km',
                    padding: {top: 0, left: 0, right: 0, bottom: 10}
                }

            },
            x: {
                display: true,
                title: {
                    display: true,
                    text: trans("chart.Model Year"),
                    padding: {top: 20, left: 0, right: 0, bottom: 0}
                },
                offset: true,
                time: {
                    unit: 'year'
                }
            }
        },

    };
    const chartInstance = new Chart(ctx, {
        type: "line",
        data: chartData,
        options: chartOptions,
    });

    const canvas = document.getElementById("js-avgPriceOdoChart");
    canvas.chartInstance = chartInstance;

    setHighlightTooltip(chartInstance, yearsArray, year);

    ctx.canvas.addEventListener('mouseout', (event) => {
        setHighlightTooltip(chartInstance, yearsArray, year);

        event.stopPropagation();
    }, true);

    const chartSpinner = document.querySelector('.js-chartSpinner');
    const avgPriceOdoTitle = document.querySelector('.js-avgPriceOdoTitle');
    const avgPriceOdoChart = document.querySelector('#js-avgPriceOdoChart');
    const avgPriceOdoChartDescription = document.querySelector('#js-avgPriceOdoChart-description');

    if (chartSpinner !== null) {
        chartSpinner.classList.add('d-none');
    }

    if (avgPriceOdoTitle !== null) {
        const car = `${make} ${model}`;
        avgPriceOdoTitle.textContent = trans("chart.Market Value Dynamics: :car", {"car": car});
        avgPriceOdoTitle.classList.remove('d-none');
    }

    if (avgPriceOdoChart !== null) {
        avgPriceOdoChart.classList.remove('d-none');
        avgPriceOdoChart.parentElement.classList.remove('d-none');
        document.getElementById("js-avgPriceOdoChart-section").classList.remove("d-none");
    }

    if (avgPriceOdoChartDescription !== null) {
        avgPriceOdoChartDescription.classList.remove('d-none');
    }
}

/**
 * Render graph to show price (€) / odometer (km)
 * @param data
 * @param make
 * @param model
 * @param year
 * @returns {boolean}
 */
export function renderPriceOdoChart({data, make, model, year}) {
    if (!data) {
        console.error("Invalid or missing data for chart rendering.");
        return false;
    }

    const odoArray = [];
    const priceArray = [];
    const marketArray = [];

    data.map((graphEntry) => {
        odoArray.push(Math.floor((graphEntry["odometer_km"] / 1000)) * 1000);
        priceArray.push(Math.floor(graphEntry["price_original"]));
        marketArray.push(graphEntry["market"]);
    });

    if (odoArray.length < minPriceVsOdoChartsResults || priceArray.length < minPriceVsOdoChartsResults) {
        return false;
    }

    const priceOdoChartElm = document.getElementById("js-priceOdoChart");
    if (priceOdoChartElm === null) {
        console.error("The element with ID 'js-priceOdoChart' was not found in the document!");
        return false;
    }
    const ctx = priceOdoChartElm.getContext("2d");

    const processedOdometerValues = odoArray.map((odoValue, index) => Math.floor(odoValue / 1000) * 1000);
    const processedPriceValues = priceArray.map(priceValue => Math.floor(priceValue));

// Create an array of unique x,y point objects for trendline calculation
    const trendData = processedOdometerValues.map((odoValue, index) => {
        return {x: odoValue, y: processedPriceValues[index]};
    });

// Sort the trendData array by x values (odometer readings)
    trendData.sort((a, b) => a.x - b.x);

// Remove duplicates on x axis by keeping only the first occurrence of each x value
    const uniqueTrendData = [];
    const seenXValues = new Set();

    for (const point of trendData) {
        if (!seenXValues.has(point.x)) {
            uniqueTrendData.push(point);
            seenXValues.add(point.x);
        }
    }

// Use Simple-statistics to create a linear regression line based on the unique points
    const trendline = ss.linearRegression(uniqueTrendData.map(point => [point.x, point.y]));

// Determine the color for each data point
    const pointColors = getPointColorTrendLine(processedOdometerValues, processedPriceValues, trendline);

    const minOdoValue = Math.min(...processedOdometerValues);
    const maxOdoValue = Math.max(...processedOdometerValues);

    // Generate evenly spaced points along the trendline at 5000km intervals
    const trendlineData = [];
    const step = 5000;

    // Calculate start and end points (rounded to nearest step)
    const startPoint = Math.floor(minOdoValue / step) * step;
    const endPoint = Math.ceil(maxOdoValue / step) * step;

    // Generate points at regular intervals
    for (let x = startPoint; x <= endPoint; x += step) {
        trendlineData.push({
            x: x,
            y: getTrendLineY(x, trendline)
        });
    }

    const config = {
        type: 'line',
        data: {
            labels: priceArray,
            datasets: [
                {
                    backgroundColor: "rgba(0, 0, 0, 0)",
                    borderColor: chartTrendline,
                    data: trendlineData,
                    fill: false,
                    yAxisID: "left-y-axis",
                    pointRadius: 0,
                    pointBackgroundColor: '#fff',
                    borderWidth: 3,

                    hoverBackgroundColor: chartTrendline,
                    hoverBorderColor: "rgba(0, 0, 0, 0.7)",
                    hoverBorderWidth: 5,
                },
                {
                    backgroundColor: "rgb(75, 192, 192)",
                    data: processedOdometerValues.map((value, index) => ({
                        x: value,
                        y: priceArray[index],
                        market: marketArray[index]
                    })),
                    fill: false,
                    yAxisID: "right-y-axis",
                    pointRadius: 3,
                    pointHoverRadius: 5,
                    showLine: false,
                    pointBackgroundColor: pointColors,
                }
            ]
        },
        options: {
            responsive: true,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        title: (item, data) => null,
                        label: (context) => {
                            const odo = context['raw'].x;
                            const price = context['raw'].y;
                            const market = context['raw'].market;

                            if (context.datasetIndex === 0) {
                                return [
                                    trans('chart.Trendline'),
                                    `${trans('chart.Price')}: ${CURRENCY}${Math.round(price).toLocaleString()}`,
                                    `${trans('chart.Odometer')}: ${odo.toLocaleString()} ${LENGTH_UNIT}`
                                ];
                            }

                            let label = [];
                            market !== undefined && label.push(trans('chart.Market: :market', {market}));
                            label.push(
                                `${trans('chart.Price')}: ${CURRENCY}${Math.round(price).toLocaleString()}`,
                                `${trans('chart.Odometer')}: ${odo.toLocaleString()} ${LENGTH_UNIT}`
                            );

                            return label;
                        }
                    }
                }
            },
            interaction: {
                intersect: false,
                mode: 'nearest'
            },
            scales: {
                'left-y-axis': {
                    type: 'linear',
                    position: 'left',
                    min: 0,
                    max: (Math.ceil(Math.max(...priceArray) * 1.1 / 1000)) * 1000,
                    ticks: {
                        stepSize: (Math.ceil(Math.max(...priceArray) * 1.1 / 1000)) * 100
                    },
                    title: {
                        display: true,
                        text: trans("chart.Price (:currency)", {"currency": CURRENCY}),
                        padding: {top: 0, left: 0, right: 0, bottom: 10}
                    },
                },
                'right-y-axis': {
                    type: 'linear',
                    position: 'right',
                    display: false,
                    min: 0,
                    max: (Math.ceil(Math.max(...priceArray) * 1.1 / 1000)) * 1000,
                    ticks: {
                        stepSize: (Math.ceil(Math.max(...priceArray) * 1.1 / 1000)) * 100,
                        autoSkip: false,
                    },
                },
                x: {
                    ticks: {
                        autoSkip: true,
                        maxTicksLimit: 10,
                    },
                    type: "linear",
                    display: true,
                    max: maxOdoValue + (maxOdoValue - minOdoValue) * 0.1,
                    title: {
                        display: true,
                        text: trans("chart.Odometer (:unit)", {"unit": LENGTH_UNIT}),
                        padding: {top: 20, left: 0, right: 0, bottom: 0}
                    },
                }
            },
        }
    };

    const chartSpinner = document.querySelector(".js-chartSpinner");
    const chartDescription = document.querySelector("#js-priceOdoChart-description");
    if (chartSpinner) {
        chartSpinner.classList.add("d-none");
        chartDescription.classList.add("d-none");
    }

    const priceOdoTitle = document.querySelector(".js-priceOdoTitle");
    if (priceOdoTitle) {
        priceOdoTitle.textContent = trans("chart.Vehicle Price Map: :car", {
            car: `${make} ${model} ${year}`,
        });
        priceOdoTitle.classList.remove("d-none");
        priceOdoTitle.parentNode?.classList.remove("d-none");
    }

    const priceOdoChart = (ctx && config) ? new Chart(ctx, config) : null;

    const canvas = document.getElementById("js-priceOdoChart")
    canvas.chartInstance = priceOdoChart;

    if (priceOdoChart) {
        const chartElement = document.querySelector("#js-priceOdoChart");
        if (chartElement) {
            chartElement.classList.remove("d-none");
            chartElement.parentNode?.classList.remove("d-none");
        }
        document.getElementById("js-priceOdoChart-section").classList.remove("d-none");
    }

    if (chartDescription) {
        chartDescription.classList.remove("d-none");
    }
}

/**
 * Render Histogram based on price.
 * @param data
 * @param make
 * @param model
 * @param year
 */
export function renderPriceHistogramChart({data, make, model, year}) {
    if (!data) {
        console.error("Invalid or missing data for chart rendering.");
        return false;
    }

    const MINIMUM_COUNT = 10;
    const ctx = document.getElementById("js-priceHistogramChart")?.getContext("2d");
    if (!ctx) {
        return false;
    }

    const priceArray = [];
    const priceTrans = trans("chart.Price (:currency)", {"currency": CURRENCY});

    // Map data
    data.map((graphEntry) => {
        priceArray.push(Math.floor(graphEntry["price_original"]));
    });

    if(priceArray.length < MINIMUM_COUNT){
        return;
    }

    const hist = histogram(priceArray);
    const binRange = hist.tickRange();
    const labels = range(binRange[0], Math.round(binRange[1]), binRange[2]);
    const bins = Array(labels.length).fill(0);

    for (let i = 0; i < priceArray.length; i++) {
        const value = priceArray[i];
        const index = labels.indexOf(hist.fun(value));
        bins[index]++;
    }

    if (priceArray.length === 0) {
        return false;
    }

    const percentile15 = calculatePercentile(0.15, priceArray);
    const percentile75 = calculatePercentile(0.75, priceArray);

    const up = (ctx, value) => ctx.p0.parsed.x < percentile15 ? value : undefined;
    const down = (ctx, value) => ctx.p0.parsed.x > percentile75 ? value : undefined;

    const chartData = {
        labels: labels,
        datasets: [
            {
                fill: true,
                borderWidth: 3,
                pointRadius: 4,
                borderRadius: 10,
                data: bins,
                tension: 0.4,
                pointBackgroundColor: '#fff',
                pointBorderColor: '#333',
                segment: {
                    backgroundColor: ctx => up(ctx, chartBelowMarket) || down(ctx, chartAboveMarket) || chartMarketAverage,
                    borderColor: '#57B1CD',
                },
            }
        ]
    }
    const config = {
        type: 'line',
        data: chartData,
        options: {
            interaction: {
                intersect: false,
                mode: 'index'
            },
            responsive: true,
            scales: {
                y: {
                    display: true,
                    title: {
                        display: true,
                        text: trans("chart.Count of vehicles"),
                        padding: 10,
                    },
                },
                x: {
                    type: 'linear',
                    ticks: {
                        stepSize: binRange[2]
                    },
                    title: {
                        display: true,
                        text: priceTrans,
                        padding: 10,
                    },
                    max: Math.round(binRange[1]) + binRange[2]
                }
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        title: (context) => {
                            return trans("chart.Number of vehicle listings in the price range");
                        },
                    },
                },
            }
        }
    }
    const chart1 = new Chart(ctx, config);

    const canvas = chart1.canvas;
    canvas.style.height = `400px`;
    chart1.update();
    canvas.chartInstance = chart1;

    const chartSpinner = document.querySelector(".js-chartSpinner");
    chartSpinner?.classList.add("d-none");

    const priceHistogramTitle = document.querySelector(".js-priceHistogramTitle");
    if (priceHistogramTitle) {
        priceHistogramTitle.textContent = trans("chart.Vehicle Price Distribution: :make :model :year", {
            make,
            model,
            year
        });
        priceHistogramTitle?.classList.remove("d-none");
    }

    const chart = document.getElementById("js-priceHistogramChart");
    const chartDescription = document.getElementById("js-priceHistogramChart-description");
    chart?.classList.remove("d-none");
    chart?.parentNode?.classList.remove("d-none");

    if(chart){
        document.getElementById("js-priceHistogramChart-section").classList.remove("d-none");
    }

    if (chartDescription) {
        chartDescription.classList.remove("d-none");
    }
}

/**
 * Render Graph to visualize odometer depends on years
 * @param data
 */
export function renderOdometerHistoryChart(data) {
    const canvas = document.getElementById('js-vehicleHistoryData');

    if (!canvas) {
        console.error("Canvas element with ID 'js-vehicleHistoryData' was not found.");
        return;
    }
    const ctx = canvas.getContext('2d');

    if (data.length < 2) {
        console.error("Not enough data to render the chart.");
        return;
    }
    const dates = [];
    const odometer = [];

    data.forEach(odometerHistory => {
        dates.push(odometerHistory.date);
        odometer.push(odometerHistory.odometer);
    });

    const chartData = {
        labels: dates,
        datasets: [{
            backgroundColor: "#2c2c2c",
            borderColor: 'rgb(75, 192, 192)',
            data: odometer,
            fill: false,
            pointRadius: 5,
            pointHoverRadius: 7,
            scaleSteps: 1,
            showLine: true,
        }]
    };
    const config = {
        type: 'line',
        data: chartData,
        options: {
            responsive: true,
            maintainAspectRatio: true,
            interaction: {
                intersect: false,
                mode: 'index'
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: 'month'
                    },
                    ticks: {
                        maxTicksLimit: 5
                    },
                    offset: true,
                    title: {
                        display: true,
                        text: trans("chart.Date"),
                    },
                },
                y: {
                    title: {
                        display: true,
                        padding: 10,
                        text: trans("chart.Odometer (:unit)", {"unit": LENGTH_UNIT}),
                    },
                }
            },
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        title: () => trans("chart.Vehicle mileage"),
                        label: function (tooltipItem, data) {
                            const date = new Date(tooltipItem.label).toLocaleDateString();
                            const mileage = tooltipItem.formattedValue + LENGTH_UNIT;
                            return date + ' - ' + mileage;
                        }
                    },
                },
            }
        }
    };
    const chart = new Chart(ctx, config);
    canvas.chartInstance = chart;

    const h2 = document.createElement('h2');
    h2.className = "Site-subheading pt-3";
    h2.style.cssText = "margin-top: 20px; text-align: center;";
    h2.innerText = trans("chart.Vehicle mileage");

    const chartElement = document.getElementById("js-vehicleHistoryData");
    if (chartElement) {
        chartElement.classList.remove("d-none");
        chartElement.parentElement.classList.remove("d-none");
        document.getElementById("js-vehicleHistoryData-section").classList.remove("d-none");
    }
}

/**
 * Renders a line chart illustrating the deprecation of car prices over quarters.
 * @param data
 */
/**
 * Renders a line chart illustrating the deprecation of car prices over quarters.
 * @param data
 */
export function renderDeprecationChart(data) {
    const canvasId = "js-deprecationChart";
    const chartElement = document.getElementById(canvasId);

    if (!chartElement) {
        console.error(`Canvas element with ID '${canvasId}' not found.`);
        return;
    }    

    if (!data || !Array.isArray(data.data)) {
        console.error("Invalid or missing data for chart rendering.");
        return;
    }

    const ctx = chartElement.getContext("2d");

    const parseQuarter = str => {
        const [year, quarter] = str.split('-').map(Number);
        return { year, quarter };
    };

    const toQuarterStr = (year, quarter) => `${year}-${quarter}`;
    const formatLabel = str => str.replace('-', ' Q');

    const getNextQuarter = ({ year, quarter }) =>
        quarter === 4 ? { year: year + 1, quarter: 1 } : { year, quarter: quarter + 1 };

    const sortedData = [...data.data].sort((a, b) => {
        const aq = parseQuarter(a.date), bq = parseQuarter(b.date);
        return (aq.year * 4 + aq.quarter) - (bq.year * 4 + bq.quarter);
    });

    const valueKey = 'avg(price_EUR)';
    const dataMap = new Map(sortedData.map(d => [d.date, d[valueKey]]));

    const first = parseQuarter(sortedData[0].date);
    const last = parseQuarter(sortedData[sortedData.length - 1].date);

    const filledData = [];
    for (let q = first; ; q = getNextQuarter(q)) {
        const quarterStr = toQuarterStr(q.year, q.quarter);
        if (dataMap.has(quarterStr)) {
            filledData.push({ date: quarterStr, price: dataMap.get(quarterStr) });
        } else {
            // Interpolate if possible
            const prev = [...dataMap.keys()].reverse().find(d => {
                const dq = parseQuarter(d);
                return (dq.year * 4 + dq.quarter) < (q.year * 4 + q.quarter);
            });

            const next = [...dataMap.keys()].find(d => {
                const dq = parseQuarter(d);
                return (dq.year * 4 + dq.quarter) > (q.year * 4 + q.quarter);
            });

            let interpolated = 0;
            if (prev && next) {
                const pQ = parseQuarter(prev), nQ = parseQuarter(next);
                const pNum = pQ.year * 4 + pQ.quarter, nNum = nQ.year * 4 + nQ.quarter;
                const cNum = q.year * 4 + q.quarter;
                const ratio = (cNum - pNum) / (nNum - pNum);
                interpolated = dataMap.get(prev) + ratio * (dataMap.get(next) - dataMap.get(prev));
            } else {
                interpolated = dataMap.get(prev || next) || 0;
            }

            filledData.push({ date: quarterStr, price: interpolated });
        }

        if (q.year === last.year && q.quarter === last.quarter) break;
    }

    const labels = filledData.map(d => formatLabel(d.date));
    const prices = filledData.map(d => d.price);

    const config = {
        type: 'line',
        data: {
            labels,
            datasets: [{
                label: trans("chart.Average price (:currency)", {"currency": CURRENCY}),
                data: prices,
                borderColor: "rgb(75, 192, 192)",
                fill: false,
            }]
        },
        options: {
            responsive: true,
            plugins: {
                legend: { display: true },
                tooltip: {
                    intersect: false,
                    mode: 'index',
                    callbacks: {
                        label: ({ parsed }) => trans("chart.Average price :currency:price", {"currency": CURRENCY, "price": Math.round(parsed.y).toLocaleString()})
                    }
                }
            },
            scales: {
                x: {
                    title: { display: true, text: "Quarters" }
                },
                y: {
                    title: { display: true, text: trans("chart.Average price (:currency)", {"currency": CURRENCY}) },
                    ticks: {
                        callback: v => `€${v.toLocaleString()}`
                    }
                }
            }
        }
    };

    const chart = new Chart(ctx, config);
    chartElement.chartInstance = chart;

    const highlightTooltip = () => {
        const lastIndex = labels.length - 1;
        chart.tooltip.setActiveElements([{ datasetIndex: 0, index: lastIndex }], { x: 0, y: 0 });
        chart.tooltip._active = chart.tooltip.getActiveElements();
        chart.render();
    };

    if ((window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth) >= 768) {
        highlightTooltip();
    }

    ctx.canvas.addEventListener('mouseout', (e) => {
        highlightTooltip();
        e.stopPropagation();
    }, true);

    document.getElementById("js-deprecationChart-description")?.classList.remove("d-none");
    chartElement.classList.remove("d-none");
    chartElement.parentElement?.classList.remove("d-none");
    document.getElementById("js-deprecationChart-section")?.classList.remove("d-none");
}


/**
 * Render Vehicle Valuation Chart
 * @param {number} min - Minimum value of the range
 * @param {number} max - Maximum value of the range
 * @param {number} value - Current value to be indicated
 * @param {number} average - Average market price
 */
export function renderVehicleValuationChart(min, max, value, average) {
    const chartSectionElement = document.getElementById('js-vehicleValuationChart-section');
    const pointerElement = document.getElementById('pointer');
    const gradientBarElement = document.getElementById('gradient-bar');
    const minValueElement = document.getElementById('min-value');
    const maxValueElement = document.getElementById('max-value');
    const rangeDescriptionElement = document.getElementById('range-description');
    const averageDescriptionElement = document.getElementById('average-description');

    if (!chartSectionElement) {
        console.error("Element with ID 'js-vehicleValuationChart-section' not found.");
        return;
    }

    if (!gradientBarElement) {
        console.error("Element with ID 'gradient-bar' not found.");
        return;
    }

    if (!pointerElement) {
        console.error("Element with ID 'pointer' not found.");
        return;
    }

    if (!minValueElement) {
        console.error("Element with ID 'min-value' not found.");
        return;
    }

    if (!maxValueElement) {
        console.error("Element with ID 'max-value' not found.");
        return;
    }

    if (!rangeDescriptionElement) {
        console.error("Element with ID 'range-description' not found.");
        return;
    }

    if (!averageDescriptionElement) {
        console.error("Element with ID 'average-description' not found.");
        return;
    }

    min = Math.round(min);
    max = Math.round(max);
    value = Math.round(value);

    minValueElement.textContent = `${min.toLocaleString()} ${CURRENCY}`;
    maxValueElement.textContent = `${max.toLocaleString()} ${CURRENCY}`;

    rangeDescriptionElement.innerHTML = trans(
        `The average market price is <strong>:min :currency - :max :currency</strong> depending on the vehicle odometer reading.`,
        { min: min.toLocaleString(), max: max.toLocaleString(), currency: CURRENCY }
    );

    averageDescriptionElement.innerHTML = trans(
        `The average market price is <strong>:value :currency</strong> depending on the vehicle odometer reading.`,
        { value: value.toLocaleString(), currency: CURRENCY }
    );

    chartSectionElement.classList.remove('d-none');

    requestAnimationFrame(() => {
        const barWidth = gradientBarElement.clientWidth;
        const pointerPosition = ((value - min) / (max - min)) * barWidth;

        pointerElement.style.left = `${pointerPosition}px`;
        pointerElement.classList.remove('d-none');
    });
}
